import { useState, memo } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import Iconify from 'src/components/Iconify';
import { fToNow } from 'src/utils/formatTime';
import { formataNumeroCelular } from 'src/utils/functions';
import { CardActionsMenu } from './components/CardActionsMenu';

const QUALIFICACAO_CONFIG = {
  1: { label: 'Baixa', color: '#3b82f6', bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
  2: { label: 'Média', color: '#f59e0b', bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  3: { label: 'Alta', color: '#f97316', bg: 'bg-orange-50 dark:bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400' },
  4: { label: 'Muito Alta', color: '#ef4444', bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
  5: { label: 'Crítica', color: '#7C3AED', bg: 'bg-violet-50 dark:bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function hashStringToColor(str) {
  if (!str) return '#7C3AED';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#7C3AED', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6'];
  return colors[Math.abs(hash) % colors.length];
}

NegociosCard.propTypes = {
  card: PropTypes.object,
  index: PropTypes.number,
  cardId: PropTypes.string,
  onSelect: PropTypes.func,
  callbackRefreshLeads: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  rules: PropTypes.object,
};

function NegociosCard({ card, index, cardId, onSelect, callbackRefreshLeads, userId, rules }) {
  const [showContactInfo, setShowContactInfo] = useState(false);

  const qual = QUALIFICACAO_CONFIG[card?.qualificacao];
  const assignedName = card?.nomeUsuario;
  const avatarColor = hashStringToColor(assignedName);
  const valor = card?.valor || 0;

  return (
    <Draggable draggableId={card?._id} index={index}>
      {(provided, snapshot) => (
        <div
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <div
            onClick={() => onSelect(cardId)}
            className={`bg-white dark:bg-[#222529] rounded-lg border border-gray-200/80 dark:border-[#383838] px-2.5 py-2 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-gray-300 dark:hover:border-[#4a4a4a] transition-all group relative ${snapshot.isDragging ? 'shadow-lg ring-2 ring-[#7C3AED]/20' : ''}`}
          >
            {/* Row 1: Qualificação fire + Nome */}
            <div className="flex items-start gap-1 mb-1">
              {qual && (
                <Iconify
                  icon="icon-park-solid:fire"
                  width={13}
                  className="shrink-0 mt-[2px]"
                  style={{ color: qual.color }}
                  title={qual.label}
                />
              )}
              <p className="text-[12px] font-semibold text-gray-900 dark:text-[#D1D2D3] leading-tight line-clamp-1 flex-1 min-w-0">
                {card?.nome || 'Sem nome'}
              </p>
              {card?.data_modificacao && (
                <span className="text-[9px] text-gray-400 dark:text-[#ABABAD] shrink-0 whitespace-nowrap mt-[2px]">
                  {fToNow(card.data_modificacao)}
                </span>
              )}
            </div>

            {/* Row 2: WhatsApp info */}
            {card?.whatsapp?.numero && (
              <div className="flex items-center gap-1 mb-1 min-w-0">
                <Iconify icon="ri:whatsapp-fill" width={11} className="text-green-500 shrink-0" />
                <span className="text-[10px] font-medium text-gray-600 dark:text-[#D1D2D3] truncate">
                  {formataNumeroCelular(card.whatsapp.numero)}
                </span>
              </div>
            )}
            {(card?.whatsapp?.nome || card?.whatsapp?.lastUpdate) && (
              <div className="flex items-center gap-1.5 mb-1 pl-[15px] min-w-0">
                {card.whatsapp.nome && (
                  <span className="text-[10px] text-gray-400 dark:text-[#ABABAD] truncate">
                    {card.whatsapp.nome}
                  </span>
                )}
                {card.whatsapp.lastUpdate && (
                  <span className="text-[9px] text-gray-400 dark:text-[#ABABAD]/60 shrink-0 flex items-center gap-0.5">
                    <Iconify icon="mdi:clock-outline" width={9} />
                    {fToNow(card.whatsapp.lastUpdate)}
                  </span>
                )}
              </div>
            )}

            {/* Valor */}
            {valor > 0 && (
              <div className="mb-1">
                <span className="text-[10px] font-bold text-gray-700 dark:text-[#D1D2D3]">
                  R$ {valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Row 3: Responsável + Actions */}
            <div className="flex items-center justify-between">
              {assignedName ? (
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0"
                    style={{ backgroundColor: avatarColor }}
                    title={assignedName}
                  >
                    {getInitials(assignedName)}
                  </div>
                  <span className="text-[10px] text-gray-500 dark:text-[#ABABAD] truncate">
                    {assignedName}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-[#383838] flex items-center justify-center border border-dashed border-gray-200 dark:border-[#4a4a4a]">
                    <Iconify icon="mdi:account-plus-outline" width={10} className="text-gray-300 dark:text-[#ABABAD]/40" />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-1 shrink-0">
                {card?.contato && (
                  <div
                    className="relative"
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => setShowContactInfo(true)}
                    onMouseLeave={() => setShowContactInfo(false)}
                  >
                    <span className="text-gray-300 dark:text-[#ABABAD]/40 hover:text-[#7C3AED] dark:hover:text-[#7C3AED] cursor-pointer transition-colors" title="Contato">
                      <Iconify icon="mdi:account-circle-outline" width={14} />
                    </span>
                    {showContactInfo && (
                      <div className="absolute bottom-full right-0 mb-1 z-50 w-[200px] p-2.5 rounded-xl shadow-xl bg-slate-50 dark:bg-[#222529] border border-gray-100 dark:border-[#383838] text-left">
                        <div className="flex flex-col gap-1.5">
                          {card.contato.nome && (
                            <div className="flex items-center gap-2">
                              <Iconify width={12} icon="fluent:person-32-filled" className="text-gray-400 shrink-0" />
                              <span className="text-[11px] text-gray-700 dark:text-[#D1D2D3] truncate">{card.contato.nome}</span>
                            </div>
                          )}
                          {card.contato.telefone && (
                            <div className="flex items-center gap-2">
                              <Iconify width={12} icon="ic:phone" className="text-gray-400 shrink-0" />
                              <span className="text-[11px] text-gray-700 dark:text-[#D1D2D3]">{formataNumeroCelular(card.contato.telefone)}</span>
                            </div>
                          )}
                          {card.contato.info?.email && (
                            <div className="flex items-center gap-2">
                              <Iconify width={12} icon="ic:email" className="text-gray-400 shrink-0" />
                              <span className="text-[11px] text-gray-700 dark:text-[#D1D2D3] break-all leading-tight">{card.contato.info.email}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {card?.closed && (
                  <span className="text-gray-400 dark:text-[#ABABAD]/50" title="Finalizado">
                    <Iconify icon="lets-icons:flag-finish" width={12} />
                  </span>
                )}

                {card?.convertido && (
                  <span title={card.convertido === 1 ? 'Venda' : 'Perda'}>
                    <Iconify
                      icon="simple-icons:cashapp"
                      width={12}
                      className={card.convertido === 1 ? 'text-green-500' : 'text-red-500'}
                    />
                  </span>
                )}

                <div onClick={(e) => e.stopPropagation()}>
                  <CardActionsMenu
                    callbackRefreshLeads={callbackRefreshLeads}
                    card={card}
                    userId={userId}
                    rules={rules}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default memo(NegociosCard);
